import { useEffect, useState } from 'preact/hooks'
import { Route, Router, Switch, useLocation } from 'wouter-preact'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AppShell } from './components/layout'
import { LoginPage } from './pages/login'
import { InboxPage } from './pages/conversations'

import { KnowledgePage } from './pages/knowledge'
import { PlaybookPage } from './pages/playbook'
import { QualityPage } from './pages/quality'
import { TunePage } from './pages/tune'
import { SettingsPage } from './pages/settings'
import { SitesPage } from './pages/sites'
import { getStoredOperator, getToken, onSessionChange, setSession } from './auth/session'
import { startDemoSession } from './api/endpoints'
import { ensureServiceWorker } from './push/register'
import { onUpdateAvailable, watchForUpdates } from './sw/updates'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 4_000,
    },
  },
})

function useIsAuthed() {
  const [authed, setAuthed] = useState<boolean>(() => !!getToken())
  useEffect(() => onSessionChange((t) => setAuthed(!!t)), [])
  return authed
}

function Root() {
  const authed = useIsAuthed()
  const [location, setLocation] = useLocation()
  const [updateAvailable, setUpdateAvailable] = useState(false)

  // Register the service worker once the app mounts — safe on every route
  useEffect(() => {
    ensureServiceWorker().catch(() => {
      /* non-fatal — handled in /settings */
    })
    watchForUpdates()
    return onUpdateAvailable(() => setUpdateAvailable(true))
  }, [])

  // Clear the app-icon badge whenever the operator opens / refocuses the
  // PWA. The service worker maintains the badge count from active push
  // notifications; once the operator is actively looking at the app, the
  // dock/home-screen number badge is just noise. Re-armed automatically
  // on the next push by the SW's refreshBadge().
  useEffect(() => {
    const clearBadge = () => {
      if (typeof navigator !== 'undefined' && 'clearAppBadge' in navigator) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(navigator as any).clearAppBadge().catch(() => { /* best-effort */ })
      }
    }
    // Clear on mount (user is here, looking).
    clearBadge()
    // Clear again whenever the tab regains focus.
    const onVisible = () => {
      if (document.visibilityState === 'visible') clearBadge()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  // Root redirect
  useEffect(() => {
    if (location === '/' || location === '') {
      setLocation(authed ? '/conversations' : '/login')
    }
  }, [location, authed])

  // ?try=demo — the public demo link from the Spoke & Sprocket storefront.
  // Never replaces a real operator's session on this device.
  const [demoStarting, setDemoStarting] = useState(
    () => new URLSearchParams(window.location.search).get('try') === 'demo'
  )
  useEffect(() => {
    if (!demoStarting) return
    const done = () => {
      const url = new URL(window.location.href)
      url.searchParams.delete('try')
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)
      setDemoStarting(false)
    }
    const current = getStoredOperator()
    if (getToken() && !current?.isDemo) {
      done()
      return
    }
    startDemoSession()
      .then((res) => {
        setSession(res.sessionToken, {
          id: res.operator.id,
          email: res.operator.email,
          name: res.operator.name,
          sites: res.sites,
          isDemo: true,
        })
        setLocation('/conversations')
      })
      .catch(() => { /* falls through to the normal sign-in screen */ })
      .finally(done)
  }, [demoStarting])

  // Guarded routes: redirect unauthed visitors to /login
  const isPublic = location === '/login' || demoStarting
  useEffect(() => {
    if (!authed && !isPublic) {
      setLocation('/login')
    }
  }, [authed, isPublic])

  return (
    <>
      {updateAvailable && <UpdateBanner />}
      {!authed && demoStarting ? null : !authed ? (
        <Switch>
          <Route path="/login" component={LoginPage} />
          <Route>
            <LoginPage />
          </Route>
        </Switch>
      ) : (
        <AppShell>
          <Switch>
            <Route path="/conversations">{() => <InboxPage />}</Route>
            <Route path="/conversations/:id">
              {(params) => <InboxPage id={params.id} />}
            </Route>
            <Route path="/settings" component={SettingsPage} />
            <Route path="/playbook" component={PlaybookPage} />
            <Route path="/knowledge" component={KnowledgePage} />
            <Route path="/quality" component={QualityPage} />
            <Route path="/tune" component={TunePage} />
            <Route path="/sites" component={SitesPage} />
            <Route>
              <InboxPage />
            </Route>
          </Switch>
        </AppShell>
      )}
    </>
  )
}

function UpdateBanner() {
  // Sticky-top alert. The SW has already activated by the time this renders
  // (sw.js calls skipWaiting); a hard reload is what loads the new HTML/JS
  // into the running page memory.
  return (
    <div class="update-banner" role="status">
      <span>A new version of Monty is available.</span>
      <button
        type="button"
        class="update-banner-btn"
        onClick={() => window.location.reload()}
      >
        Refresh
      </button>
    </div>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Root />
      </Router>
    </QueryClientProvider>
  )
}
